import React, { useCallback } from 'react';
import { FormProvider } from 'react-hook-form';

/**
 * Form — root form wrapper.
 *
 * Wraps RHF's FormProvider + a native <form>, owns submit handling and
 * automatically scrolls to the first invalid field on a failed submission.
 *
 * The caller is responsible for calling useForm() and passing the result as
 * `methods`. This keeps form state accessible in the parent (e.g. for dirty
 * checking, programmatic resets, or reading values).
 *
 * Usage:
 *   const methods = useForm({ defaultValues, resolver: yupResolver(schema) });
 *
 *   <Form methods={methods} onSubmit={async (data) => saveData(data)}>
 *     <FormSection title="Details">
 *       <FormRow>
 *         <FormField name="name" label="Name" xs={12} md={8} />
 *       </FormRow>
 *     </FormSection>
 *     <FormActions>
 *       <Button type="submit" variant="contained">Save</Button>
 *     </FormActions>
 *   </Form>
 *
 * Props:
 *   methods   {object}   — return value of useForm() (required)
 *   onSubmit  {function} — async (data) => void, called on valid submission
 *   children  {node}
 *   sx        — forwarded to the <form> element as style (use style= not sx=)
 *   ...rest   — any other native <form> props (id, autoComplete, etc.)
 */
export default function Form({ methods, onSubmit, children, style, ...rest }) {
  const scrollToFirstError = useCallback(() => {
    // Give the DOM a tick to reflect the new aria-invalid attributes before
    // searching for the first invalid element.
    requestAnimationFrame(() => {
      const firstInvalid =
        document.querySelector('[aria-invalid="true"]') ||
        document.querySelector('.Mui-error input, .Mui-error textarea');
      if (firstInvalid) {
        firstInvalid.scrollIntoView({ block: 'center', behavior: 'smooth' });
        firstInvalid.focus({ preventScroll: true });
      }
    });
  }, []);

  const handleSubmit = methods.handleSubmit(
    async (data) => {
      await onSubmit?.(data);
    },
    () => {
      // Invalid submission — scroll to first error field
      scrollToFirstError();
    }
  );

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', ...style }}
        {...rest}
      >
        {children}
      </form>
    </FormProvider>
  );
}
